import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Sparkles, Plus, Trash2, ChevronDown, RefreshCw, AlertTriangle } from 'lucide-react';
import * as aiChatApi from '../../api/aiChat';
import { executeAction as executeActionApi } from '../../api/aiChat';
import * as aiBillingApi from '../../api/aiBilling';

// ═══════════════════════════════════════════
// Simple markdown renderer for AI messages
// ═══════════════════════════════════════════

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      elements.push(
        <pre key={elements.length} className="bg-gray-800 text-green-300 text-xs rounded p-2 my-1.5 overflow-x-auto whitespace-pre-wrap">
          {codeLines.join('\n')}
        </pre>
      );
      continue;
    }

    // Empty line
    if (!line.trim()) {
      i++;
      continue;
    }

    // Bullet list
    if (/^[\-\*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[\-\*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[\-\*]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={elements.length} className="list-disc list-inside ml-1 my-1 space-y-0.5">
          {items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={elements.length} className="list-decimal list-inside ml-1 my-1 space-y-0.5">
          {items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
        </ol>
      );
      continue;
    }

    // Regular paragraph
    elements.push(<p key={elements.length} className="my-0.5">{renderInline(line)}</p>);
    i++;
  }

  return elements;
}

function renderInline(text) {
  // Bold: **text**
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const codeMatch = remaining.match(/`([^`]+)`/);

    let firstMatch = null;
    let firstIdx = Infinity;

    if (boldMatch && boldMatch.index < firstIdx) {
      firstMatch = { type: 'bold', match: boldMatch };
      firstIdx = boldMatch.index;
    }
    if (codeMatch && codeMatch.index < firstIdx) {
      firstMatch = { type: 'code', match: codeMatch };
      firstIdx = codeMatch.index;
    }

    if (!firstMatch) {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    const m = firstMatch.match;
    if (m.index > 0) {
      parts.push(<span key={key++}>{remaining.slice(0, m.index)}</span>);
    }

    if (firstMatch.type === 'bold') {
      parts.push(<strong key={key++} className="font-semibold">{m[1]}</strong>);
    } else {
      parts.push(<code key={key++} className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{m[1]}</code>);
    }

    remaining = remaining.slice(m.index + m[0].length);
  }

  return parts;
}

// ═══════════════════════════════════════════
// Suggested prompts
// ═══════════════════════════════════════════

const SUGGESTED_PROMPTS = [
  'Summarize this case',
  'What documents are missing?',
  'Are there any conflicts between documents?',
  "What's the current status and next steps?",
  "Compare the police report with the client's statement",
];

// ═══════════════════════════════════════════
// Action Confirm Card (proposed AI field updates)
// ═══════════════════════════════════════════

function ActionConfirmCard({ action, onConfirm, onDismiss, loading }) {
  const hasFields = action.field_updates?.length > 0;
  const hasExt = action.extended_data && Object.keys(action.extended_data).length > 0;

  return (
    <div className="mx-0 my-1.5 rounded-lg border border-amber-200 bg-amber-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-200 bg-amber-100/60">
        <span className="text-sm">✏️</span>
        <span className="text-xs font-semibold text-amber-800">Proposed Update</span>
        {action.narrative && (
          <span className="text-xs text-amber-600 italic truncate flex-1">{action.narrative}</span>
        )}
      </div>

      {/* Field updates */}
      {hasFields && (
        <div className="px-3 py-2 space-y-1">
          <p className="text-[10px] text-amber-700 font-medium uppercase tracking-wide mb-1.5">Field Changes</p>
          {action.field_updates.map((fu, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-gray-600 font-medium">{fu.field_name}</span>
              <span className="text-gray-800 bg-white px-2 py-0.5 rounded border border-amber-200 font-mono text-[11px]">
                {fu.display_value || String(fu.value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Extended data flags */}
      {hasExt && (
        <div className="px-3 py-2 border-t border-amber-100 space-y-1">
          <p className="text-[10px] text-amber-700 font-medium uppercase tracking-wide mb-1.5">Case Intelligence Flags</p>
          {Object.entries(action.extended_data).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{k.replace(/_/g, ' ')}</span>
              <span className="text-gray-700 font-medium">{String(v)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Confirm / Dismiss buttons */}
      <div className="flex gap-2 px-3 py-2 border-t border-amber-200 bg-white">
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 py-1.5 text-xs font-semibold text-white bg-green-500 hover:bg-green-600 rounded-md disabled:opacity-50 transition-colors"
        >
          {loading ? 'Saving...' : '✓ Confirm Update'}
        </button>
        <button
          onClick={onDismiss}
          disabled={loading}
          className="flex-1 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 transition-colors"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// AIChatPanel Component
// ═══════════════════════════════════════════

export default function AIChatPanel({ recordId, onClose, referenceNumber }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [indexStatus, setIndexStatus] = useState(null);
  const [showConvDropdown, setShowConvDropdown] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [deepAnalysis, setDeepAnalysis] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null); // { plan_name, blocked, ... }
  const [billingWarning, setBillingWarning] = useState(null);
  const [executingAction, setExecutingAction] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Load conversations, index status, and billing on mount
  useEffect(() => {
    if (!recordId) return;
    loadConversations();
    loadIndexStatus();
    loadBillingStatus();
  }, [recordId]);

  const loadBillingStatus = async () => {
    try {
      const data = await aiBillingApi.getUsage();
      setBillingStatus(data.subscription);
    } catch (err) { /* fail-open: ignore */ }
  };

  const loadConversations = async () => {
    try {
      const data = await aiChatApi.getConversations(recordId);
      setConversations(data.conversations || []);
      // Auto-select most recent conversation
      if (data.conversations && data.conversations.length > 0 && !activeConvId) {
        selectConversation(data.conversations[0].id);
      }
    } catch (err) { /* ignore */ }
  };

  const loadIndexStatus = async () => {
    try {
      const data = await aiChatApi.getRecordStatus(recordId);
      setIndexStatus(data);
    } catch (err) { /* ignore */ }
  };

  const selectConversation = async (convId) => {
    setActiveConvId(convId);
    setShowConvDropdown(false);
    setLoading(true);
    try {
      const data = await aiChatApi.getMessages(convId);
      const mappedMessages = (data.messages || []).map(m => ({
        ...m,
        pendingAction: m.pending_action
          ? { ...m.pending_action, action_id: m.action_id, status: m.action_status }
          : null,
      }));
      setMessages(mappedMessages);
    } catch (err) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const createNewConversation = async () => {
    try {
      const data = await aiChatApi.createConversation(recordId);
      const conv = data.conversation;
      setConversations(prev => [conv, ...prev]);
      setActiveConvId(conv.id);
      setMessages([]);
      setShowConvDropdown(false);
      setDeepAnalysis(false);
    } catch (err) { /* ignore */ }
  };

  const handleDeleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await aiChatApi.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConvId === convId) {
        setActiveConvId(null);
        setMessages([]);
      }
    } catch (err) { /* ignore */ }
  };

  // Auto-scroll on new messages
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setAutoScroll(atBottom);
  }, []);

  // Send message
  const handleSend = async (text) => {
    const content = (text || inputValue).trim();
    if (!content || sending) return;

    // Create conversation if none exists
    let convId = activeConvId;
    if (!convId) {
      try {
        const data = await aiChatApi.createConversation(recordId);
        convId = data.conversation.id;
        setActiveConvId(convId);
        setConversations(prev => [data.conversation, ...prev]);
      } catch (err) { return; }
    }

    // Add user message optimistically
    const userMsg = { id: 'temp-' + Date.now(), role: 'user', content, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setSending(true);
    setAutoScroll(true);

    try {
      const result = await aiChatApi.sendMessage(convId, content, deepAnalysis);

      // Handle blocked response (billing limit reached)
      if (result.blocked) {
        setMessages(prev => prev.filter(m => m.id !== userMsg.id)); // Remove optimistic msg
        setBillingWarning(null);
        setBillingStatus(result.subscription || billingStatus);
        const blockedMsg = {
          id: 'blocked-' + Date.now(),
          role: 'system',
          content: result.message,
          created_at: new Date().toISOString(),
          isBlocked: true,
        };
        setMessages(prev => [...prev, blockedMsg]);
        setSending(false);
        return;
      }

      // Handle billing warning
      if (result.warning) {
        setBillingWarning(result.warning);
      }

      // Add AI response
      const aiMsg = {
        id: 'ai-' + Date.now(),
        role: 'assistant',
        content: result.message,
        sources: result.sources || [],
        created_at: new Date().toISOString(),
        pendingAction: result.pendingAction || null,
      };
      setMessages(prev => [...prev, aiMsg]);

      // Refresh billing status after each query
      loadBillingStatus();

      // Update conversation title if this is the first message
      if (conversations.length > 0) {
        loadConversations();
      }
    } catch (err) {
      const errorMsg = {
        id: 'error-' + Date.now(),
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setSending(false);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    try {
      await aiChatApi.reindexRecord(recordId);
      // Poll status after a delay
      setTimeout(loadIndexStatus, 3000);
      setTimeout(loadIndexStatus, 8000);
      setTimeout(() => { loadIndexStatus(); setReindexing(false); }, 15000);
    } catch (err) {
      setReindexing(false);
    }
  };

  // Execute (confirm/dismiss) a pending AI write action
  const handleExecuteAction = async (actionId, confirmed) => {
    try {
      setExecutingAction(actionId);
      const result = await executeActionApi(actionId, confirmed);
      // Update the message in state to reflect action is done
      setMessages(prev => prev.map(m => {
        if (m.pendingAction?.action_id === actionId) {
          return { ...m, pendingAction: { ...m.pendingAction, status: result.status } };
        }
        return m;
      }));
      if (confirmed && result.status === 'confirmed') {
        const successMsg = {
          id: 'action-success-' + Date.now(),
          role: 'system',
          content: `✓ Updated ${result.field_count || 0} field${result.field_count !== 1 ? 's' : ''}${result.extended_data_count > 0 ? ` + ${result.extended_data_count} flag${result.extended_data_count !== 1 ? 's' : ''}` : ''}.`,
          isActionConfirmed: true,
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, successMsg]);
      }
    } catch (err) {
      console.error('executeAction error:', err);
    } finally {
      setExecutingAction(null);
    }
  };

  // Key handler
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Index status badge
  const renderIndexBadge = () => {
    if (!indexStatus) return null;
    if (reindexing) {
      return <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-600 border border-yellow-200">Indexing...</span>;
    }
    if (indexStatus.totalChunks > 0) {
      return (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
          {indexStatus.fileCount} doc{indexStatus.fileCount !== 1 ? 's' : ''} indexed
        </span>
      );
    }
    return <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-400 border border-gray-200">No documents</span>;
  };

  return (
    <div className="w-[380px] h-full border-l border-gray-200 bg-white flex flex-col shadow-lg flex-shrink-0
                     max-md:fixed max-md:inset-0 max-md:w-full max-md:z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-gray-800 truncate">AI Assistant</h3>
          {referenceNumber && <span className="text-xs text-gray-400 font-mono">{referenceNumber}</span>}
        </div>
        <div className="flex items-center gap-1">
          {renderIndexBadge()}
          <button onClick={handleReindex} disabled={reindexing}
            className="p-1.5 text-gray-400 hover:text-purple-500 rounded disabled:opacity-50" title="Reindex documents">
            <RefreshCw className={`h-3.5 w-3.5 ${reindexing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Conversation selector */}
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
        <div className="relative flex-1">
          <button onClick={() => setShowConvDropdown(!showConvDropdown)}
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 truncate max-w-[200px]">
            {activeConvId
              ? (conversations.find(c => c.id === activeConvId)?.title || 'Conversation').substring(0, 30)
              : 'No conversation'
            }
            <ChevronDown className="h-3 w-3 flex-shrink-0" />
          </button>

          {showConvDropdown && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
              {conversations.map(c => (
                <div key={c.id}
                  onClick={() => selectConversation(c.id)}
                  className={`flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 ${
                    c.id === activeConvId ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                  }`}>
                  <span className="truncate flex-1">{(c.title || 'Conversation').substring(0, 35)}</span>
                  <button onClick={(e) => handleDeleteConversation(c.id, e)}
                    className="p-0.5 text-gray-300 hover:text-red-500 flex-shrink-0 ml-1">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {conversations.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400">No conversations yet</div>
              )}
            </div>
          )}
        </div>
        <button onClick={createNewConversation}
          className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium flex-shrink-0">
          <Plus className="h-3 w-3" /> New
        </button>
      </div>

      {/* Messages area */}
      <div ref={messagesContainerRef} onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {loading && (
          <div className="text-center text-xs text-gray-400 py-8">Loading messages...</div>
        )}

        {/* Suggested prompts — shown when no messages */}
        {!loading && messages.length === 0 && (
          <div className="py-6 space-y-3">
            <div className="text-center">
              <Sparkles className="h-8 w-8 text-purple-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Ask me anything about this case</p>
              <p className="text-xs text-gray-400 mt-1">I can analyze documents, find information, and identify issues</p>
            </div>
            <div className="space-y-1.5 mt-4">
              {SUGGESTED_PROMPTS.map((prompt, i) => (
                <button key={i} onClick={() => handleSend(prompt)}
                  className="w-full text-left px-3 py-2 text-xs text-gray-600 bg-gray-50 hover:bg-purple-50 hover:text-purple-700 rounded-lg border border-gray-100 hover:border-purple-200 transition-colors">
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map(msg => (
          <React.Fragment key={msg.id}>
            {/* Action confirmed success pill */}
            {msg.isActionConfirmed && (
              <div className="flex justify-center my-1">
                <span className="text-[11px] px-3 py-1 rounded-full bg-green-50 text-green-600 border border-green-100">
                  {msg.content}
                </span>
              </div>
            )}
            {!msg.isActionConfirmed && (
              <div className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {/* Blocked message */}
                {msg.isBlocked && (
                  <div className="w-full">
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        <span className="font-medium">Usage Limit Reached</span>
                      </div>
                      <p className="text-xs">{msg.content}</p>
                    </div>
                  </div>
                )}
                {msg.role === 'assistant' && !msg.isBlocked && (
                  <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="h-3 w-3 text-purple-500" />
                  </div>
                )}
                {!msg.isBlocked && <div className={`max-w-[85%] flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`rounded-lg px-3 py-2 text-sm leading-relaxed break-words ${
                    msg.role === 'user'
                      ? 'bg-blue-50 text-gray-800'
                      : 'bg-gray-50 text-gray-800 border border-gray-100'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="prose-sm">{renderMarkdown(msg.content)}</div>
                    ) : (
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                    )}
                  </div>

                  {/* Source citations */}
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {msg.sources.map((src, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-purple-50 text-purple-600 rounded-full border border-purple-100">
                          <span>📄</span> {(src.file_name || 'Document').substring(0, 25)}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Pending action confirm card */}
                  {msg.role === 'assistant' && msg.pendingAction && msg.pendingAction.status === 'pending' && (
                    <ActionConfirmCard
                      action={msg.pendingAction}
                      onConfirm={() => handleExecuteAction(msg.pendingAction.action_id, true)}
                      onDismiss={() => handleExecuteAction(msg.pendingAction.action_id, false)}
                      loading={executingAction === msg.pendingAction.action_id}
                    />
                  )}
                </div>}
              </div>
            )}
          </React.Fragment>
        ))}

        {/* AI typing indicator */}
        {sending && (
          <div className="flex gap-2">
            <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-3 w-3 text-purple-500" />
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-400">{deepAnalysis ? 'Analyzing all documents...' : 'Analyzing documents...'}</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom */}
      {!autoScroll && messages.length > 0 && (
        <button onClick={() => { setAutoScroll(true); messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
          className="absolute bottom-20 right-6 p-1.5 bg-white border border-gray-200 rounded-full shadow-md text-gray-400 hover:text-gray-600 z-10">
          <ChevronDown className="h-4 w-4" />
        </button>
      )}

      {/* Billing warning banner */}
      {billingWarning && (
        <div className="mx-3 mb-0 flex items-center gap-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-700">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="flex-1">{billingWarning}</span>
          <button onClick={() => setBillingWarning(null)} className="text-yellow-400 hover:text-yellow-600">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* No-plan full overlay */}
      {billingStatus?.plan_name === 'none' && (
        <div className="border-t border-gray-200 px-3 py-4 bg-gray-50 flex-shrink-0 text-center">
          <p className="text-sm text-gray-500 mb-1">AI features are not enabled</p>
          <p className="text-xs text-gray-400">Contact your administrator to enable AI assistance</p>
        </div>
      )}

      {/* Input area */}
      {billingStatus?.plan_name !== 'none' && <div className="border-t border-gray-200 px-3 py-2 bg-white flex-shrink-0">
        {/* Mode toggle pill */}
        <div className={`flex rounded-lg border mb-2 ${sending ? 'opacity-50 pointer-events-none' : ''} ${
          deepAnalysis ? 'border-purple-200' : 'border-gray-200'
        }`}>
          <button
            onClick={() => setDeepAnalysis(false)}
            disabled={sending}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-l-lg transition-all ${
              !deepAnalysis
                ? 'bg-gray-100 text-gray-700 border-r border-gray-200'
                : 'text-gray-400 hover:text-gray-600 border-r border-gray-200'
            }`}
          >
            <span>⚡</span> Quick Ask
          </button>
          <button
            onClick={() => setDeepAnalysis(true)}
            disabled={sending}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-r-lg transition-all ${
              deepAnalysis
                ? 'bg-purple-50 text-purple-700 shadow-[0_0_8px_rgba(147,51,234,0.1)]'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <span>🔍</span> Deep Analysis
          </button>
        </div>
        {deepAnalysis && (
          <p className="text-[10px] text-purple-400 mb-1.5 -mt-1">All documents will be reviewed — slower, more thorough</p>
        )}
        <div className="flex items-end gap-2">
          <textarea ref={inputRef} value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={sending ? 'Waiting for response...' : 'Ask about this case...'}
            disabled={sending}
            className="flex-1 resize-none border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-purple-300
                       max-h-[96px] min-h-[36px] leading-5 disabled:bg-gray-50 disabled:text-gray-400"
            rows={1}
            style={{ height: 'auto', minHeight: '36px', maxHeight: '96px' }}
            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px'; }}
          />
          <button onClick={() => handleSend()}
            disabled={!inputValue.trim() || sending}
            className="p-1.5 text-purple-500 hover:text-purple-600 rounded flex-shrink-0 disabled:text-gray-300 disabled:cursor-not-allowed"
            title="Send">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>}
    </div>
  );
}
